import { api } from './api';

// ============ Types ============

export interface ReviewRequest {
    id: string;
    _id?: string;
    taskId?: string;
    contentItemId?: string;
    deliverableId?: string;
    projectId?: string;
    title: string;
    description?: string;
    status: 'pending' | 'in_review' | 'changes_requested' | 'approved' | 'rejected';
    priority: 'low' | 'medium' | 'high' | 'urgent';
    createdBy: string;
    assignedTo?: string;
    reviewers: string[];
    dueDate?: string;
    reviewedAt?: string;
    reviewedBy?: string;
    teamId: string;
    decision?: {
        action: 'approved' | 'rejected' | 'changes_requested';
        comment?: string;
        decidedBy: string;
        decidedAt: string;
    };
    attachments: Array<{
        fileName: string;
        fileUrl: string;
        fileType?: string;
        uploadedBy: string;
        uploadedAt: string;
    }>;
    createdAt: string;
    updatedAt: string;
}

export interface Comment {
    id: string;
    _id?: string;
    reviewRequestId: string;
    parentId?: string;
    authorId: string;
    content: string;
    mentions: string[];
    resolved: boolean;
    resolvedBy?: string;
    resolvedAt?: string;
    reactions: Array<{
        userId: string;
        type: 'like' | 'dislike';
    }>;
    attachments: Array<{
        fileName: string;
        fileUrl: string;
        fileType?: string;
    }>;
    isEdited: boolean;
    editedAt?: string;
    createdAt: string;
    updatedAt: string;
    children?: Comment[]; // For threaded comments
}

export interface CollaborationStats {
    pending: number;
    inReview: number;
    changesRequested: number;
    approved: number;
    rejected: number;
    totalComments: number;
    unresolvedComments: number;
}

export interface ReviewRequestFilters {
    status?: string;
    assignedTo?: string;
    createdBy?: string;
    projectId?: string;
    taskId?: string;
    priority?: string;
}

// ============ API Service ============

export const collaborationService = {
    // Review Requests
    async getReviews(filters?: ReviewRequestFilters): Promise<ReviewRequest[]> {
        const params = new URLSearchParams();
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value) params.append(key, value);
            });
        }
        const response = await api.get(`/collaboration/reviews?${params.toString()}`);
        return response.data.map((r: any) => ({ ...r, id: r._id || r.id }));
    },

    async getMyReviews(): Promise<ReviewRequest[]> {
        const response = await api.get('/collaboration/reviews/my');
        return response.data.map((r: any) => ({ ...r, id: r._id || r.id }));
    },

    async getPendingApprovals(): Promise<ReviewRequest[]> {
        const response = await api.get('/collaboration/reviews/pending');
        return response.data.map((r: any) => ({ ...r, id: r._id || r.id }));
    },

    async getReview(id: string): Promise<ReviewRequest> {
        const response = await api.get(`/collaboration/reviews/${id}`);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    async createReview(data: {
        taskId?: string;
        projectId?: string;
        title: string;
        description?: string;
        priority?: string;
        assignedTo?: string;
        reviewers?: string[];
        dueDate?: string;
    }): Promise<ReviewRequest> {
        const response = await api.post('/collaboration/reviews', data);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    async updateReview(id: string, data: Partial<ReviewRequest>): Promise<ReviewRequest> {
        const response = await api.patch(`/collaboration/reviews/${id}`, data);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    async deleteReview(id: string): Promise<void> {
        await api.delete(`/collaboration/reviews/${id}`);
    },

    async makeDecision(id: string, decision: {
        action: 'approved' | 'rejected' | 'changes_requested';
        comment?: string;
    }): Promise<ReviewRequest> {
        const response = await api.post(`/collaboration/reviews/${id}/decision`, decision);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    async assignReviewer(id: string, data: {
        assignedTo: string;
        reviewers?: string[];
    }): Promise<ReviewRequest> {
        const response = await api.post(`/collaboration/reviews/${id}/assign`, data);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    // Comments
    async getComments(reviewRequestId: string): Promise<Comment[]> {
        const response = await api.get(`/collaboration/reviews/${reviewRequestId}/comments`);
        const mapComments = (comments: any[]): Comment[] => {
            return comments.map((c: any) => ({
                ...c,
                id: c._id || c.id,
                children: c.children ? mapComments(c.children) : [],
            }));
        };
        return mapComments(response.data);
    },

    async createComment(reviewRequestId: string, data: {
        content: string;
        parentId?: string;
        mentions?: string[];
    }): Promise<Comment> {
        const response = await api.post(`/collaboration/reviews/${reviewRequestId}/comments`, data);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    async updateComment(id: string, data: {
        content?: string;
        resolved?: boolean;
    }): Promise<Comment> {
        const response = await api.patch(`/collaboration/comments/${id}`, data);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    async deleteComment(id: string): Promise<void> {
        await api.delete(`/collaboration/comments/${id}`);
    },

    async resolveComment(id: string): Promise<Comment> {
        const response = await api.post(`/collaboration/comments/${id}/resolve`);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    async addReaction(id: string, type: 'like' | 'dislike'): Promise<Comment> {
        const response = await api.post(`/collaboration/comments/${id}/reactions`, { type });
        return { ...response.data, id: response.data._id || response.data.id };
    },

    async removeReaction(id: string): Promise<Comment> {
        const response = await api.delete(`/collaboration/comments/${id}/reactions`);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    // Stats
    async getStats(): Promise<CollaborationStats> {
        const response = await api.get('/collaboration/stats');
        return response.data;
    },

    // Integration
    async sendForReview(data: {
        taskId: string;
        projectId: string;
        title: string;
        description?: string;
        assignedTo?: string;
    }): Promise<ReviewRequest> {
        const response = await api.post('/collaboration/send-for-review', data);
        return { ...response.data, id: response.data._id || response.data.id };
    },
};
